import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Search, UserPlus, Check, X, MessageSquare } from "lucide-react";

export default function Friends() {
  const nav = useNavigate();
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [f, r, o] = await Promise.all([
        api.get("/friends"),
        api.get("/friends/requests"),
        api.get("/friends/outgoing"),
      ]);
      setFriends(f.data);
      setRequests(r.data);
      setOutgoing(o.data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
        setResults(r.data);
      } catch (e) {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const sendRequest = async (uid) => {
    setMsg("");
    try {
      await api.post(`/friends/request/${uid}`);
      setMsg("Friend request sent");
      load();
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };
  const accept = async (id) => {
    await api.post(`/friends/accept/${id}`);
    load();
  };
  const decline = async (id) => {
    await api.post(`/friends/decline/${id}`);
    load();
  };

  return (
    <div className="space-y-6 fade-up">
      <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">Circle · 圈子</p>
      <h1 className="font-serif text-4xl">Your inner circle.</h1>

      {/* Search */}
      <div className="relative">
        <Search size={16} strokeWidth={1.5} className="absolute left-0 top-3 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search members by name…"
          className="editorial-input pl-7"
          data-testid="friends-search"
        />
      </div>
      {results.length > 0 && (
        <div className="border border-white/10 rounded-md divide-y divide-white/5" data-testid="friends-search-results">
          {results.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-white/40 mt-0.5">{u.occupation} · {u.region}</div>
              </div>
              <button
                onClick={() => sendRequest(u.id)}
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] hover:text-[#f3e5ab]"
                data-testid={`friends-request-${u.id}`}
              >
                <UserPlus size={14} strokeWidth={1.5} /> Connect
              </button>
            </div>
          ))}
        </div>
      )}
      {msg && <p className="text-sm text-white/60" data-testid="friends-msg">{msg}</p>}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {["friends", "incoming", "outgoing"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] -mb-px border-b ${
              tab === t ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50"
            }`}
            data-testid={`friends-tab-${t}`}
          >
            {t === "friends" ? `Friends · ${friends.length}` : t === "incoming" ? `Requests · ${requests.length}` : `Sent · ${outgoing.length}`}
          </button>
        ))}
      </div>

      {tab === "friends" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="friends-list">
          {friends.length === 0 && <p className="text-white/40">No connections yet.</p>}
          {friends.map((f) => (
            <div key={f.id} className="border border-white/10 rounded-md p-4 bg-[#141414]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{f.name}</div>
                  <div className="text-xs text-white/40 mt-0.5 truncate">{f.occupation} · {f.region}</div>
                </div>
                <button
                  onClick={() => nav(`/messages?to=${f.id}`)}
                  className="shrink-0 text-[#d4af37] hover:text-[#f3e5ab] p-1.5"
                  title="Message"
                  data-testid={`friends-message-${f.id}`}
                >
                  <MessageSquare size={16} strokeWidth={1.5} />
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(f.hobbies || []).slice(0, 4).map((h) => (
                  <span key={h} className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 border border-[#d4af37]/30 px-2 py-0.5 rounded-full">
                    {h.replace("_", " ")}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "incoming" && (
        <div className="space-y-2" data-testid="friends-incoming">
          {requests.length === 0 && <p className="text-white/40">No pending requests.</p>}
          {requests.map((r) => (
            <div key={r.id} className="flex items-center justify-between border border-white/10 rounded-md p-4 bg-[#141414]">
              <div>
                <div className="font-medium">{r.user?.name}</div>
                <div className="text-xs text-white/40 mt-0.5">{r.user?.occupation}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => accept(r.id)} className="text-[#d4af37] p-2" data-testid={`friends-accept-${r.id}`}>
                  <Check size={18} strokeWidth={1.5} />
                </button>
                <button onClick={() => decline(r.id)} className="text-white/50 p-2" data-testid={`friends-decline-${r.id}`}>
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "outgoing" && (
        <div className="space-y-2" data-testid="friends-outgoing">
          {outgoing.length === 0 && <p className="text-white/40">No outgoing requests.</p>}
          {outgoing.map((r) => (
            <div key={r.id} className="border border-white/10 rounded-md p-4 bg-[#141414]">
              <div className="font-medium">{r.user?.name}</div>
              <div className="text-xs text-white/40 mt-0.5">Awaiting response…</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
